import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { z } from 'zod';

import { getChannelDefaults } from '@/components/pages/job-posting/utils/channel-defaults';
import { MIN_CHANNEL_BUDGET } from '@/components/pages/job-posting/validation/constants';
import { useBudgetType } from '@/hooks/use-budget-type';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';
import { getChannelName } from '@/utils/channel-names';
import { durationInDays, toDate, today } from '@/utils/date-util';

interface UseEditCampaignSchemaParams {
  channelSlug?: string;
  budgetSpent?: number | null;
  isCompleted?: boolean;
  /** Campaign is read-only — every field is disabled, so skip all validation. */
  isReadonly?: boolean;
  /** Free campaign — the budget field is read-only, so skip budget validation. */
  isFree?: boolean;
}

const campaignBaseSchema = z.object({
  starts_at: z.string().optional(),
  end_at: z.string().nullable().optional(),
  budget: z.number().nullable().optional(),
  budget_type: z.enum(['daily', 'weekly', 'monthly', 'lifetime']).optional(),
  end_at_type: z.enum(['fixed', 'target']).nullable().optional(),
  expected_cpa: z.number().nullable().optional(),
  expected_conversion: z.number().nullable().optional(),
  total_budget: z.number().optional(),
  target_applicants: z.number().nullable().optional(),
  target_hires: z.number().nullable().optional(),
  reach_campaign: z
    .object({
      end_at: z.string().nullable().optional(),
      budget: z.number().nullable().optional(),
      expected_conversion: z.number().nullable().optional(),
      expected_cpa: z.number().nullable().optional(),
      reach_campaign_id: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  is_editable: z.boolean().optional(),
  is_location_sensitive: z.boolean().optional(),
});

type CampaignSchemaData = z.infer<typeof campaignBaseSchema>;

/**
 * Builds a Zod schema for the edit-campaign form.
 *
 * Mirrors the validation rules from the job-posting channel schema
 * (minBudget, minDuration, end-date-after-start-date) while also
 * enforcing that the lifetime budget cannot drop below already-spent.
 */
export function useEditCampaignSchema({
  channelSlug,
  budgetSpent,
  isCompleted = false,
  isReadonly = false,
  isFree = false,
}: UseEditCampaignSchemaParams) {
  const intl = useIntl();
  const { formatCurrency } = useCurrencySymbol();
  const { calculateTotalBudget } = useBudgetType();

  const channelDefaults = channelSlug
    ? getChannelDefaults(channelSlug)
    : undefined;
  const minBudget = channelDefaults
    ? (channelDefaults.minBudget ?? MIN_CHANNEL_BUDGET)
    : undefined;
  const minDuration =
    channelDefaults?.minDuration ?? channelDefaults?.duration ?? 1;
  const maxDuration = channelDefaults?.maxDuration;
  const channelName = channelSlug ? getChannelName(channelSlug) : '';
  const actualBudgetSpent = budgetSpent ?? 0;

  return useMemo(
    () =>
      campaignBaseSchema.superRefine((data: CampaignSchemaData, ctx) => {
        // Skip all validation when the form is locked — completed and read-only
        // campaigns have every field disabled, so there is nothing to fix.
        if (isCompleted || isReadonly) return;

        const { budget, budget_type: budgetType } = data;
        const isLifetime = !budgetType || budgetType === 'lifetime';

        // The budget field is read-only for free campaigns, so skip budget validation.
        if (
          !isFree &&
          isLifetime &&
          budget &&
          actualBudgetSpent > 0 &&
          budget < actualBudgetSpent
        ) {
          ctx.addIssue({
            code: 'custom',
            message: intl.formatMessage(
              {
                defaultMessage:
                  'Enter at least {amount}, the amount already spent.',
                description:
                  'Validation: lifetime budget must be at least spend to date; {amount} is the formatted spent total',
              },
              {
                amount: formatCurrency(actualBudgetSpent, 'USD', {
                  exact: true,
                }),
              },
            ),
            path: ['budget'],
          });
        }

        if (!isFree && budget && minBudget) {
          const { starts_at: startsAt, end_at: endAt } = data;
          let effectiveTotalBudget: number;

          if (isLifetime || !startsAt || !endAt) {
            effectiveTotalBudget = budget;
          } else {
            const campaignStart = toDate(startsAt);
            const remainingStartDate =
              campaignStart > today ? startsAt : today.toISOString();

            effectiveTotalBudget = Math.round(
              actualBudgetSpent +
                calculateTotalBudget({
                  budgetType: budgetType ?? 'lifetime',
                  budgetPerUnit: budget,
                  startDate: remainingStartDate,
                  endDate: endAt,
                }),
            );
          }

          if (effectiveTotalBudget < minBudget) {
            ctx.addIssue({
              code: 'custom',
              message: intl.formatMessage(
                {
                  defaultMessage:
                    'Total campaign budget of {totalBudget} is less than the minimum required budget of {minBudget} for {channelName}',
                  description:
                    'Error when the computed total campaign budget (spend + projected) falls below the channel minimum',
                },
                {
                  totalBudget: formatCurrency(effectiveTotalBudget),
                  channelName,
                  minBudget: formatCurrency(minBudget),
                },
              ),
              path: ['budget'],
            });
          }
        }

        const { starts_at: startsAt, end_at: endAt } = data;

        if (startsAt && endAt) {
          const startDate = toDate(startsAt);
          startDate.setHours(0, 0, 0, 0);
          const endDate = toDate(endAt);
          endDate.setHours(0, 0, 0, 0);

          if (endDate <= startDate) {
            ctx.addIssue({
              code: 'custom',
              message: intl.formatMessage({
                defaultMessage: 'End date must be after start date',
                description:
                  'Validation: campaign end date cannot be on or before the start date',
              }),
              path: ['end_at'],
            });
            return;
          }

          const duration = durationInDays(startsAt, endAt);
          if (minDuration && duration < minDuration) {
            ctx.addIssue({
              code: 'custom',
              message: intl.formatMessage(
                {
                  defaultMessage:
                    'Minimum campaign duration is {minDuration} days',
                  description:
                    'Validation: campaign must run for at least the channel-required number of days',
                },
                { minDuration },
              ),
              path: ['end_at'],
            });
          }

          // Same start→end span as create (`validateMaxDuration`). A from-today
          // ceiling blocked scheduled campaigns whose window still fit maxDuration.
          if (maxDuration && duration > maxDuration) {
            ctx.addIssue({
              code: 'custom',
              message: intl.formatMessage(
                {
                  defaultMessage:
                    'Maximum campaign duration is {maxDuration} days',
                  description:
                    'Validation: campaign cannot run longer than the channel-allowed number of days',
                },
                { maxDuration },
              ),
              path: ['end_at'],
            });
          }
        }
      }),
    [
      intl,
      formatCurrency,
      calculateTotalBudget,
      minBudget,
      minDuration,
      maxDuration,
      channelName,
      actualBudgetSpent,
      isCompleted,
      isReadonly,
      isFree,
    ],
  );
}
